// SNIGJ - Digital Signature Component
import React, { useRef, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { 
  PenTool, RotateCcw, Check, X, Upload, Download, Shield, 
  FileSignature, AlertCircle, CheckCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const DigitalSignature = ({ 
  documentId, 
  documentTitle,
  onSignatureComplete,
  requirePin = true,
  showVerification = true 
}) => {
  const { user, language } = useAuth();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [pin, setPin] = useState('');
  const [signing, setSigning] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignatureData(null);
  };

  const handleSignDocument = async () => {
    if (!hasSignature) {
      toast.error(language === 'fr' ? 'Veuillez dessiner votre signature' : 'Please draw your signature');
      return;
    }

    if (requirePin && !pin) {
      toast.error(language === 'fr' ? 'Veuillez entrer votre code PIN' : 'Please enter your PIN code');
      return;
    }

    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureImage = canvas.toDataURL('image/png');
      
      // Create signature hash for verification
      const signatureHash = await generateSignatureHash(signatureImage, user.user_id, documentId);
      
      const response = await api.post('/documents/sign', {
        document_id: documentId,
        signature_image: signatureImage,
        signature_hash: signatureHash,
        pin: requirePin ? pin : null,
        signed_at: new Date().toISOString()
      });

      setSignatureData({
        signature_id: response.data.signature_id,
        hash: signatureHash,
        signed_at: response.data.signed_at,
        signer_name: user.name,
        signer_role: user.role
      });

      toast.success(language === 'fr' ? 'Document signé avec succès' : 'Document signed successfully');
      
      if (onSignatureComplete) {
        onSignatureComplete(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de signature' : 'Signature error'));
    } finally {
      setSigning(false);
    }
  };

  const generateSignatureHash = async (signatureImage, userId, docId) => {
    const data = `${signatureImage}-${userId}-${docId}-${Date.now()}`;
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const verifySignature = async () => {
    try {
      const response = await api.get(`/documents/${documentId}/verify-signature`);
      setVerificationStatus(response.data);
    } catch (error) {
      setVerificationStatus({ valid: false, error: error.response?.data?.detail });
    }
  };

  return (
    <Card className="w-full max-w-lg" data-testid="digital-signature">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSignature className="w-5 h-5 text-primary" />
          {language === 'fr' ? 'Signature Numérique' : 'Digital Signature'}
        </CardTitle>
        <CardDescription>
          {documentTitle && (
            <span className="text-primary font-medium">{documentTitle}</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Signature Canvas */}
        <div className="space-y-2">
          <Label>{language === 'fr' ? 'Dessinez votre signature' : 'Draw your signature'}</Label>
          <div className="relative border-2 border-dashed border-muted-foreground/30 rounded-lg p-1 bg-white">
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              className="w-full cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              data-testid="signature-canvas"
            />
            <div className="absolute bottom-2 left-2 right-2 border-t border-gray-300" />
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={clearSignature} className="gap-1">
              <RotateCcw className="w-4 h-4" />
              {language === 'fr' ? 'Effacer' : 'Clear'}
            </Button>
          </div>
        </div>

        {/* PIN Input */}
        {requirePin && (
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Code PIN de confirmation' : 'Confirmation PIN Code'}</Label>
            <Input
              type="password"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••••"
              className="text-center text-2xl tracking-widest"
              data-testid="signature-pin"
            />
            <p className="text-xs text-muted-foreground">
              {language === 'fr' 
                ? 'Entrez votre code PIN à 6 chiffres pour confirmer la signature'
                : 'Enter your 6-digit PIN to confirm the signature'}
            </p>
          </div>
        )}

        {/* Signer Info */}
        <div className="p-3 bg-muted rounded-lg text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {language === 'fr' ? 'Signataire:' : 'Signer:'}
            </span>
            <span className="font-medium">{user?.name}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-muted-foreground">
              {language === 'fr' ? 'Fonction:' : 'Role:'}
            </span>
            <Badge variant="outline">{user?.role}</Badge>
          </div>
        </div>

        {/* Sign Button */}
        <Button 
          onClick={handleSignDocument} 
          disabled={!hasSignature || signing || (requirePin && pin.length < 4)}
          className="w-full gap-2"
          data-testid="sign-document-btn"
        >
          {signing ? (
            <>
              <Clock className="w-4 h-4 animate-spin" />
              {language === 'fr' ? 'Signature en cours...' : 'Signing...'}
            </>
          ) : (
            <>
              <PenTool className="w-4 h-4" />
              {language === 'fr' ? 'Signer le document' : 'Sign Document'}
            </>
          )}
        </Button>

        {/* Signature Result */}
        {signatureData && (
          <>
            <Separator />
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800 dark:text-green-200">
                  {language === 'fr' ? 'Document signé' : 'Document Signed'}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ID Signature:</span>
                  <span className="font-mono text-xs">{signatureData.signature_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Hash:</span>
                  <span className="font-mono text-xs truncate max-w-[200px]">{signatureData.hash?.substring(0, 16)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span>{new Date(signatureData.signed_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Verification Status */}
        {showVerification && verificationStatus && (
          <div className={`p-4 rounded-lg border ${verificationStatus.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-2">
              {verificationStatus.valid ? (
                <Shield className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              <span className={verificationStatus.valid ? 'text-green-800' : 'text-red-800'}>
                {verificationStatus.valid 
                  ? (language === 'fr' ? 'Signature vérifiée' : 'Signature Verified')
                  : (language === 'fr' ? 'Signature non valide' : 'Invalid Signature')}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DigitalSignature;
